import "./Technology.css";
import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRightOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import useIntersection from "../useIntersection";
import patentPdf1 from "../../증/10-2023-0000425_전자등록증(10-2547584).pdf";
import patentPdf2 from "../../증/10-2023-0009412_특허증(10-2632591).pdf";
import patentPdf3 from "../../증/10-2024-0026120_전자등록증(10-2934725).pdf";
import patentPdf4 from "../../증/10-2024-0039394_특허증(10-2860403).pdf";
import pendingPatentPdf1 from "../../증/심사중/10-2026-0142968_20260731_출원번호통지서 및 출원서.pdf";
import pendingPatentPdf2 from "../../증/심사중/10-2026-0133010_20260720_분할출원보고.pdf";
import patentPreview1 from "../../증/previews/patent-1.jpg";
import patentPreview2 from "../../증/previews/patent-2.jpg";
import patentPreview3 from "../../증/previews/patent-3.jpg";
import patentPreview4 from "../../증/previews/patent-4.jpg";

const technologies = [
  {
    image: "assets/section-4--grid-1.png",
    title: "Artificial Intelligence",
    koreanTitle: "인공지능",
    description:
      "세계 최고 수준의 독자 시계열 강화학습 및 경량 LLM 기술을 보유하고 있습니다.",
  },
  {
    image: "assets/section-4--grid-img-2.png",
    title: "Market Opportunity",
    koreanTitle: "시장 기회 탐지",
    description: "최신 시장 동향과 기회를 자동으로 탐지합니다.",
  },
  {
    image: "assets/product/etfport_logo2.png",
    title: "EMP Shannon",
    koreanTitle: "AI 자산분배",
    description:
      "고객 투자 유니버스와 시장 상황을 동시에 고려하여 높은 수익률과 안정성을 함께 추구하는 포트폴리오를 구성합니다.",
    detailUrl: "/emp",
  },
];

const patents = [
  {
    image: patentPreview3,
    document: patentPdf3,
    title: "로보어드바이저를 이용한 위험 관리 장치 및 방법",
    registration: "특허 제10-2934725호",
    date: "등록일 2026.02.27",
    status: "등록",
  },
  {
    image: patentPreview4,
    document: patentPdf4,
    title: "가격 상관관계와 콘텐츠의 관련성을 동시에 반영한 뉴스 추천 장치 및 방법",
    registration: "특허 제10-2860403호",
    date: "등록일 2025.09.11",
    status: "등록",
  },
  {
    image: patentPreview2,
    document: patentPdf2,
    title: "펀드 추천 장치 및 펀드 추천 방법",
    registration: "특허 제10-2632591호",
    date: "등록일 2024.01.29",
    status: "등록",
  },
  {
    image: patentPreview1,
    document: patentPdf1,
    title: "연금 투자를 위한 변동성 최소화 기반 포트폴리오 관리 방법 및 장치",
    registration: "특허 제10-2547584호",
    date: "등록일 2023.06.21",
    status: "등록",
  },
  {
    document: pendingPatentPdf1,
    title: "위험도 기반 적응형 투자 상한 생성 방법 및 장치",
    registration: "출원번호 10-2026-0142968",
    date: "출원일 2026.07.31",
    applicationNumber: "10-2026-0142968",
    applicationDate: "2026.07.31",
    status: "출원 완료",
  },
  {
    document: pendingPatentPdf2,
    title: "EMP 운용 장치 및 방법",
    registration: "출원번호 10-2026-0133010",
    date: "출원일 2026.07.20",
    applicationNumber: "10-2026-0133010",
    applicationDate: "2026.07.20",
    status: "출원 완료",
  },
];

function Technology(props) {
  const sectionRef = useRef(null);
  const sectionInViewport = useIntersection(sectionRef, "0px");
  const [showSection, setShowSection] = useState(false);
  const [showPatents, setShowPatents] = useState(false);

  useEffect(() => {
    if (sectionInViewport) {
      setShowSection(true);
    }
  }, [sectionInViewport]);

  useEffect(() => {
    if (!showPatents) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setShowPatents(false);
    };
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [showPatents]);

  return (
    <div className="technology_root" ref={props.refProp}>
      <section
        className={`technology_section${
          showSection ? " technology_section_show" : ""
        }`}
        ref={sectionRef}
      >
        <div className="technology_inner">
          <header className="technology_header">
            <h2>Technology</h2>
          </header>

          <div className="technology_grid">
            {technologies.map((technology) => (
              <article
                className={`technology_item${
                  technology.detailUrl ? " technology_item_emp" : ""
                }`}
                key={technology.title}
              >
                <div className="technology_icon">
                  <img src={technology.image} alt={technology.title} />
                </div>
                <h3>{technology.title}</h3>
                <strong>{technology.koreanTitle}</strong>
                <p>{technology.description}</p>
                {technology.detailUrl && (
                  <a
                    href={technology.detailUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    상세 보기
                    <ArrowRightOutlined aria-hidden="true" />
                  </a>
                )}
              </article>
            ))}
          </div>

          <div className="technology_patent_cta">
            <div className="technology_patent_cta_text">
              <SafetyCertificateOutlined aria-hidden="true" />
              <div>
                <strong>딥트레이드 보유 특허</strong>
                <span>등록 특허 4건과 출원 완료 2건을 확인해 보세요.</span>
              </div>
            </div>
            <button
              className="technology_patent_button"
              type="button"
              onClick={() => setShowPatents(true)}
            >
              특허 6건 보기
              <ArrowRightOutlined aria-hidden="true" />
            </button>
          </div>

        </div>
      </section>

      {showPatents && (
        <div
          className="patent_modal_backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowPatents(false);
          }}
        >
          <section
            className="patent_modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="patent-modal-title"
          >
            <header className="patent_modal_header">
              <div>
                <h2 id="patent-modal-title">딥트레이드 보유 특허</h2>
                <p>등록 특허 4건과 출원 완료 2건을 최신순으로 확인해 보세요.</p>
              </div>
              <button
                className="patent_modal_close"
                type="button"
                aria-label="특허 목록 닫기"
                onClick={() => setShowPatents(false)}
              >
                ×
              </button>
            </header>

            <Swiper
              className="patent_slider"
              modules={[Navigation]}
              slidesPerView={1}
              spaceBetween={18}
              navigation={true}
              breakpoints={{
                760: { slidesPerView: 2, spaceBetween: 22 },
                1200: { slidesPerView: 3, spaceBetween: 24 },
              }}
            >
              {patents.map((patent, index) => (
                <SwiperSlide key={patent.registration}>
                  <figure
                    className={`patent_card${
                      patent.status === "출원 완료" ? " patent_card_pending" : ""
                    }`}
                  >
                    <div className="patent_number">
                      {String(index + 1).padStart(2, "0")}
                    </div>
                    <div className="patent_image_wrap">
                      {patent.image ? (
                        <a
                          href={patent.document}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`${patent.title} 크게 보기`}
                        >
                          <img
                            src={patent.image}
                            alt={patent.title}
                            onError={(event) => {
                              event.currentTarget.hidden = true;
                              event.currentTarget.parentElement.removeAttribute(
                                "href"
                              );
                              event.currentTarget.parentElement.setAttribute(
                                "aria-disabled",
                                "true"
                              );
                              event.currentTarget.nextElementSibling.hidden =
                                false;
                            }}
                          />
                          <span className="patent_placeholder" hidden>
                            특허증 미리보기를 불러올 수 없습니다.
                          </span>
                        </a>
                      ) : (
                        <a
                          className="patent_pending_link"
                          href={patent.document}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`${patent.title} 출원 자료 보기`}
                        >
                          <div className="patent_pending_visual">
                            <SafetyCertificateOutlined
                              className="patent_pending_watermark"
                              aria-hidden="true"
                            />
                            <div className="patent_pending_document_header">
                              <span>DEEPTRADE TECHNOLOGIES</span>
                              <em>출원 완료</em>
                            </div>
                            <div className="patent_pending_heading">
                              <small>PATENT APPLICATION</small>
                              <strong>특 허 출 원</strong>
                            </div>
                            <div className="patent_pending_invention">
                              <small>발명의 명칭</small>
                              <strong>{patent.title}</strong>
                            </div>
                            <dl className="patent_pending_details">
                              <div>
                                <dt>출원번호</dt>
                                <dd>{patent.applicationNumber}</dd>
                              </div>
                              <div>
                                <dt>출원일</dt>
                                <dd>{patent.applicationDate}</dd>
                              </div>
                              <div>
                                <dt>출원인</dt>
                                <dd>딥트레이드테크놀로지스 주식회사</dd>
                              </div>
                            </dl>
                            <div className="patent_pending_document_footer">
                              <span>DEEPTRADE TECHNOLOGIES</span>
                              <small>특허 심사 절차 진행 중</small>
                            </div>
                            <p className="patent_pending_disclaimer">
                              본 카드는 출원 사실을 요약한 안내용 이미지이며
                              특허등록증이 아닙니다.
                            </p>
                          </div>
                        </a>
                      )}
                    </div>
                    <figcaption>
                      <em className={`patent_status patent_status_${patent.status === "출원 완료" ? "pending" : "registered"}`}>
                        {patent.status}
                      </em>
                      <strong>{patent.title}</strong>
                      <span>{patent.registration}</span>
                      <span>{patent.date}</span>
                    </figcaption>
                  </figure>
                </SwiperSlide>
              ))}
            </Swiper>
          </section>
        </div>
      )}
    </div>
  );
}

export default Technology;
